import { Button, IconButton } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import {
    isDragActive,
    motion,
    isValidMotionProp,
    AnimatePresence
} from 'framer-motion';
import { Ref, useEffect, useRef, useState } from 'react';
import { useBoolean } from '@chakra-ui/hooks';
import { CircularProgress } from '@chakra-ui/progress';
import {
    AlarmIndicator,
    ChevronDownIcon,
    DotsIcons,
    FolderIcon,
    MotionBox
} from '../../sharedCode/components/UiElements';
import { Tooltip } from '@chakra-ui/tooltip';
import usePersistStore from '../../sharedCode/stores/persistStore';
import { tooltipAnimation } from '../../sharedCode/components/customTheme';
import React from 'react';
import layoutStore from '../../sharedCode/stores/layoutStore';
import {
    DndItem,
    InsertIndicatorLine
} from '../../sharedCode/components/ItemWrapper';
import {
    Menu,
    chakra,
    shouldForwardProp,
    MenuButton,
    MenuList,
    MenuItem,
    Popover,
    PopoverTrigger,
    PopoverContent
} from '@chakra-ui/react';
import EditableTitle from '../../sharedCode/components/EditableTitle';
import {
    updateTabGroup,
    updateTabGroupEmoji,
    updateTabGroupMultipleProps,
    updateTabGroupTitle
} from '../browserAPI';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { Portal } from '@chakra-ui/portal';
import ActiveInfoPopover from './ActiveInfoPopover';
import { folderColorProvider } from '../../sharedCode/Utils/colorProvider';
import { useSortable } from '@dnd-kit/sortable';
import useSelectStore from '../../sharedCode/stores/selectStore';
import useDndStore from '../../sharedCode/stores/dndStore';
import { useItemSize } from '../../sharedCode/Utils/useItemSize';

interface GroupProps {
    index: number;
    title?: string;
    color?: string;
    emoji?: string;
    id?: string;
    tabsInside: any[];
    windowId?: number;
    collapsed?: boolean;
}

/* const ChakraBox = chakra(motion.div, {
    shouldForwardProp: (prop) =>
        isValidMotionProp(prop) || shouldForwardProp(prop)
}); */

const ActiveTabGroup = ({
    index,
    title,
    color,
    emoji,
    id,
    tabsInside,
    windowId,
    collapsed
}: GroupProps) => {
    const [isHover, hover] = useBoolean();
    const [loading, setLoadingState] = useState(false);
    const hoverSide = layoutStore((state) => state.hoverSide);
    //const sidePanelIsOpen = layoutStore((state) => state.sidePanelIsOpen);
    const [isOpen, setIsOpen] = useState(!collapsed);
    const [keepInfoPopoverClosed, setKeepInfoPopoverClosed] = useState(false);
    const isSelected = useSelectStore((state) =>
        state.selectedIds.includes(id)
    );
    const insertPosition = useDndStore((state) => state.insertPosition[id]);
    // console.log('active tab group, insert pos ', insertPosition, id);

    //const [disableOpen, setDisableOpen] = useState(false);

    useEffect(() => {
        if (isOpen === !collapsed) return;
        setIsOpen(!collapsed);
    }, [collapsed]);
    const toggleOpen = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
        updateTabGroup(id, { collapsed: isOpen }, 'collapsed', windowId + '');
    };

    const handleClick = (e) => {
        useSelectStore
            .getState()
            .handleSelect(id, e, windowId + '', 'activeGroup');
    };

    let bglight = folderColorProvider(color, 0.24, -10);
    let bg36 = folderColorProvider(color, 0.36);
    //let insideGroupBg = folderColorProvider(color, 1, -40);
    const bgColor = bglight; //useColorModeValue('#FFF', '#1e2123');
    const draggingShadow = useColorModeValue(
        '0px 0px 0px 2px #44E18C',
        '0px 0px 0px 2px #44E18C'
    );
    /* const combineShadow = useColorModeValue(
        '0px 0px 0px 2px #44E18C',
        '0px 0px 0px 2px #44E18C'
    ); */
    //const shadowOne = folderColorProvider(color, 0.3, 100);
    //const shadowTwo = folderColorProvider(color, 0.1, 60);
    const hoverShadow = useColorModeValue(
        '', //'0 2px 4px -1px ' + shadowOne,
        '' //'0 3px 10px -3px rgba(0,0,0,0.90), 0 0px 0px 1px rgb(0 0 0 / 20%)'
    );
    //const countColor = folderColorProvider(color, 0.8, 100);
    const buttonHoverBg = useColorModeValue(bglight, '#FFFFFF20');
    const buttonActiveBg = useColorModeValue(bg36, '#FFFFFF10');

    const submitNewTitle = (newTitle) => {
        if (title !== newTitle) {
            let options = {};
            let hasEmoji = emoji.length ? true : false;
            let browserTitle = hasEmoji ? emoji + ' ' + newTitle : newTitle;
            if (newTitle.length === 0) {
                //uiTitle = 'Untitled folder';
                browserTitle = hasEmoji ? emoji : 'Untitled folder';
            }
            options['title'] = browserTitle;
            updateTabGroupMultipleProps(
                id,
                options,
                hasEmoji,
                emoji,
                newTitle,
                windowId + ''
            );
        }
    };

    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: id,
        data: {
            type: 'tab',
            groupId: windowId + '',
            data: { id: id, title: title, emoji: emoji, color: color },
            isActiveTab: true,
            isActiveGroup: true,
            numberOfTabsInside: tabsInside.length
        }
    });

    //Trying to solve bug for quick toggle open/close
    /* const chevronFlip = (dir, state) => {
        if (state === 'complete') {
            setDisableOpen(false);
        } else if (state === 'start') {
            setDisableOpen(true);
        }
    }; */

    return (
        <>
            <Box
                id={id + 'dropTarget'}
                ref={setNodeRef}
                //w={hoverSide ? 'auto' : '52px'}
                minW="52px"
                role="group"
                pos="relative"
                onMouseEnter={hover.on}
                onMouseOver={hover.on}
                onMouseLeave={hover.off}
                bg={
                    isDragging || isOpen || isSelected ? bgColor : 'transparent'
                }
                p="2px 0px 0px"
                overflow={'hidden'}
                //border="1px solid"
                //borderColor="#00000010"
                alignItems="center"
                my="2px"
                rounded="10px"
                cursor={'grab'}
                _active={{ cursor: 'grabbing' }}
                _hover={
                    isSelected || isDragging || isOpen
                        ? undefined
                        : {
                              boxShadow: loading ? '' : hoverShadow,
                              cursor: 'grab',
                              zIndex: 300,
                              bg: bgColor
                          }
                }
                onClick={(e) => handleClick(e)}
                transition="0.17s cubic-bezier(0.22,0.61,0.36,1)"
                boxShadow={
                    isDragging
                        ? draggingShadow
                        : isSelected || insertPosition === 'center'
                        ? '0px 0px 0px 2px ' + bg36
                        : ''
                }
                {...attributes}
                {...listeners}
            >
                <ActiveInfoPopover
                    title={title}
                    type="group"
                    tabsInside={tabsInside}
                    tab={{ id: id }}
                    groupColor={color}
                    groupEmoji={emoji}
                    groupIsOpen={isOpen}
                    keepClosed={keepInfoPopoverClosed}
                    index={index}
                >
                    <Flex alignItems="center" p="0px 12px 2px">
                        <GroupIcon
                            icon={emoji}
                            color={color}
                            currentTitle={title}
                            windowId={windowId}
                            id={id}
                            setKeepInfoPopoverClosed={setKeepInfoPopoverClosed}
                        />
                        <Tooltip
                            label="Edit title"
                            zIndex="500000"
                            placement="top-start"
                            rounded="6px"
                            motionProps={tooltipAnimation}
                            //mr="64px"
                        >
                            <Flex
                                //pos="absolute"
                                //top="8px"
                                //left="60px"
                                maxWidth="calc(100% - 58px)"
                                //isTruncated
                                py="3px"
                                w="100%"
                                //ml="-3px"
                                //maxW="100%"
                                overflow={hoverSide ? 'visible' : 'hidden'}
                                // opacity={hoverSide ? 1 : 0}
                                // transition={'0.12s'}
                                // transitionDelay={'0.08s'}
                            >
                                <EditableTitle
                                    //ml={'16px'}
                                    title={title}
                                    size={'sm'}
                                    onSubmit={submitNewTitle}
                                />
                            </Flex>
                        </Tooltip>
                        {hoverSide && (
                            <Button
                                bg="transparent"
                                size="sm"
                                ml="auto"
                                //ml={isHover ? '2px' : 'auto'}
                                p="0"
                                rounded="8px"
                                _hover={{ bg: buttonHoverBg }}
                                _active={{ bg: buttonActiveBg }}
                                transition={'0.1s'}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={toggleOpen}
                                //isDisabled={disableOpen}
                            >
                                {isHover || isOpen ? (
                                    <motion.div
                                        initial={{ scale: 0, y: -2 }}
                                        animate={{
                                            scale: 1,
                                            rotate: isOpen ? 180 : 0,
                                            y: isOpen ? -1 : 0
                                        }}
                                        /* onAnimationStart={(dir) =>
                                        chevronFlip(dir, 'start')
                                    }
                                    onAnimationComplete={(dir) =>
                                        chevronFlip(dir, 'complete')
                                    } */
                                    >
                                        <Icon
                                            as={ChevronDownSmallIcon}
                                            boxSize={2}
                                        />
                                    </motion.div>
                                ) : (
                                    <Text
                                        fontSize="13px"
                                        fontWeight="500"
                                        maxWidth="16px"
                                        //letterSpacing="0.64px"
                                        opacity={0.5}
                                    >
                                        {tabsInside.length}
                                    </Text>
                                )}
                            </Button>
                        )}
                    </Flex>
                </ActiveInfoPopover>
                <AnimatePresence initial={false}>
                    {isOpen && !isDragging && (
                        <motion.section
                            key="content"
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                                open: { height: 'auto' },
                                collapsed: { height: 0 }
                            }}
                            transition={{
                                duration: 0.12
                            }}
                        >
                            <motion.div
                                variants={{
                                    collapsed: { opacity: 0, y: 0 },
                                    open: {
                                        opacity: 1,
                                        y: 0,
                                        transition: {
                                            duration: 0.12,
                                            delay: 0.08
                                        }
                                    }
                                }}
                                transition={{ duration: 0.12, delay: 0 }}
                            >
                                {tabsInside.map((tab, i) => (
                                    <ActiveInfoPopover
                                        key={i}
                                        title={tab.title || 'Untitled'}
                                        type="tab"
                                        tab={tab}
                                        index={i}
                                        insideGroup={true}
                                    >
                                        <DndItem
                                            groupId={windowId + ''}
                                            data={tab}
                                            isActiveTab
                                            insideActiveGroup={bglight}
                                            width={'250px'}
                                        />
                                    </ActiveInfoPopover>
                                ))}
                            </motion.div>
                        </motion.section>
                    )}
                </AnimatePresence>
                <InsertIndicatorLine side={insertPosition} />
            </Box>
        </>
    );
};

export const ChevronDownSmallIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const DotsVerticalIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '14px', height: '14px', margin: 'auto' }}
        >
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
    );
};

export default ActiveTabGroup;

export const GroupIcon = ({
    icon,
    color,
    currentTitle,
    windowId,
    id,
    setKeepInfoPopoverClosed
}) => {
    const hoverSide = layoutStore((state) => state.hoverSide);
    const colorMode = useColorMode().colorMode;
    let chosenColor = folderColorProvider(color);
    let chosenColorLight = folderColorProvider(color, 1, -10);
    let chosenColorDark = folderColorProvider(color);
    let folderBg = folderColorProvider(
        color,
        0.24
        //  colorMode === 'light' ? -100 : -6
    );
    let emojiBg = folderColorProvider(
        color,
        1,
        colorMode === 'light' ? -80 : 0
    );
    let emojiShadow = folderColorProvider(
        color,
        colorMode === 'light' ? 0.28 : 0.44,
        60
    );
    const ballBg = useColorModeValue(chosenColorLight, chosenColorDark);
    const shadowOne = folderColorProvider(color, 0.4, 100);
    const shadowTwo = folderColorProvider(color, 0.25, 100);
    const outerShadow = useColorModeValue(
        '0 1px 4px -1px ' + shadowOne + ', 0 1px 2px 0px ' + shadowTwo,
        ''
    );
    const isSelected = false; //TODO
    const size = '32px';
    //Emoji picker stuff
    const [isOpen, setIsOpen] = useState(false);
    const open = () => {
        setIsOpen(!isOpen);
        setKeepInfoPopoverClosed(true);
    };
    const close = () => {
        setIsOpen(false);
        setKeepInfoPopoverClosed(false);
    };
    const emojiCategory = useColorModeValue('gray.200', '#FFF');
    const emojiPickerShadow = useColorModeValue(
        '0 12px 36px -8px rgba(48,49,53,0.36)',
        '0 4px 18px -2px rgba(0,0,0,0.80)'
    );
    const onEmojiClick = (emoji) => {
        if (emoji.native) {
            let browerTitle;
            if (currentTitle.length !== 0) {
                browerTitle = emoji.native + ' ' + currentTitle;
            } else {
                browerTitle = emoji.native + ' ' + 'Untitled folder';
            }
            updateTabGroupEmoji(id, browerTitle, emoji.native, windowId);
        }
        close();
    };

    const { activeGroupMy } = useItemSize();

    const buttonRef = useRef() as Ref<HTMLButtonElement>;

    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

    const popoverWidth = 354; // Width of the MotionBox
    const popoverHeight = 200; // Height of the MotionBox, adjust this value as needed

    const openAndCalculatePosition = (e) => {
        //e.stopPropagation();
        const { x, y } = calculatePopoverPosition(buttonRef, popoverHeight);
        setPopoverPosition({ x, y });
        open();
    };

    return (
        <>
            <Tooltip
                label="Edit"
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
            >
                <Box
                    mr="12px"
                    pos="relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Flex
                        ref={buttonRef}
                        as={Button}
                        _hover={{
                            bg: icon.length ? emojiBg : folderBg,
                            filter: 'brightness(0.925)'
                        }}
                        transition="0.15s"
                        alignItems="center"
                        rounded="8px"
                        minWidth={size}
                        minHeight={size}
                        bg={icon.length ? emojiBg : folderBg}
                        boxShadow={
                            isSelected ? '0 0px 0px 0px rgba(78,80,81,0.0)' : '' //outerShadow
                        }
                        my={activeGroupMy}
                        pos="relative"
                        maxHeight={size}
                        maxWidth={size}
                        alignContent={'center'}
                        cursor={'pointer'}
                        p="0"
                        onClick={(e) => openAndCalculatePosition(e)}
                        _active={{ transform: 'scale(0.9325)' }}
                    >
                        {!icon.length ? (
                            <FolderIcon
                                size={'20px'}
                                color={ballBg}
                                m={'auto'}
                            />
                        ) : (
                            <>
                                <Box
                                    pos="absolute"
                                    top="21px"
                                    right="7px"
                                    h="6px"
                                    w="18px"
                                    rounded={'50%'}
                                    filter={'blur(2px)'}
                                    bg={emojiShadow}
                                    zIndex={2}
                                />
                                <Text
                                    m="auto"
                                    fontWeight={500}
                                    fontSize="18px"
                                    zIndex={3}
                                >
                                    {icon}
                                </Text>
                            </>
                        )}
                    </Flex>
                    {isOpen && (
                        <Portal>
                            <MotionBox
                                //onClick={(e) => e.stopPropagation()}
                                initial={{ opacity: 0, y: -8, x: 0 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    x: hoverSide ? 0 : -8,
                                    transition: {
                                        x: { delay: 0.125, duration: 0.14 }
                                    }
                                }}
                                ml={'16px'}
                                zIndex={100}
                                rounded="12px"
                                borderWidth="0px"
                                _focus={{ outline: 'none' }}
                                boxShadow={emojiPickerShadow}
                                border="var(--chakra-colors-teBorder)"
                                w="354px"
                                overflow="hidden"
                                style={{
                                    position: 'absolute',
                                    top: popoverPosition.y,
                                    left: popoverPosition.x
                                }}
                            >
                                <Picker
                                    data={data}
                                    emojiVersion={1}
                                    color={emojiCategory}
                                    // /* emojiButtonSize={30}
                                    //     emojiSize={20}
                                    // emojiButtonRadius={'4px'}
                                    previewPosition={'none'}
                                    title="Select a emoji"
                                    emoji=""
                                    native={true}
                                    showPreview={false}
                                    onEmojiSelect={onEmojiClick}
                                    onClickOutside={close}
                                    theme={
                                        colorMode === 'light' ? 'light' : 'dark'
                                    }
                                />
                            </MotionBox>
                        </Portal>
                    )}
                </Box>
            </Tooltip>
        </>
    );
};

const calculatePopoverPosition = (buttonRef, popoverHeight) => {
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const { bottom, left, width } = buttonRect;

    const padding = 8; // Padding around the screen
    const popoverWidth = 354; // Width of the MotionBox

    let x = left + window.scrollX + width / 2 - popoverWidth / 2; // Center the popover horizontally
    const y = bottom + window.scrollY + 4; // Position the popover just below the button

    // Adjust x if the popover would go off the left or right edge of the screen
    if (x < padding) {
        x = padding;
    } else if (x + popoverWidth > window.innerWidth - padding) {
        x = window.innerWidth - popoverWidth - padding;
    }

    return { x, y };
};
